import './ui/styles/ui.css';
import { Sim } from './game/simulation';
import { bootGame } from './game/ThreeGame';
import { InfoCard } from './ui/hud/InfoCard';
import { Hotbar } from './ui/hud/Hotbar';
import { StorageBar } from './ui/hud/StorageBar';
import { HintToast } from './ui/hud/HintToast';
import { Objective } from './ui/hud/Objective';
import { TripBar } from './ui/hud/TripBar';
import { PlotToolbar } from './ui/hud/PlotToolbar';
import { ExitPlotButton } from './ui/hud/ExitPlotButton';
import { ZoomExitButton } from './ui/hud/ZoomExitButton';
import { FpsCounter } from './ui/hud/FpsCounter';
import { TileProgressBars } from './ui/hud/TileProgressBars';
import { ZonesPanel } from './ui/hud/ZonesPanel';
import { PlotPanel } from './ui/hud/PlotPanel';
import { ControlsCard } from './ui/hud/ControlsCard';
import { SettingsButton } from './ui/hud/SettingsButton';
import { PanelManager } from './ui/core/PanelManager';
import { SeedsPanel } from './ui/panels/SeedsPanel';
import { BuildPanel } from './ui/panels/BuildPanel';
import { StoragePanel } from './ui/panels/StoragePanel';
import { TruckPanel } from './ui/panels/TruckPanel';
import { MailboxPanel } from './ui/panels/MailboxPanel';
import { MillPanel } from './ui/panels/MillPanel';
import { SkillsPanel } from './ui/panels/SkillsPanel';
import { ToolRackPanel } from './ui/panels/ToolRackPanel';
import { PlotConfirmPanel } from './ui/panels/PlotConfirmPanel';
import { SettingsPanel } from './ui/panels/SettingsPanel';
import { FlyToHud } from './ui/FlyToHud';
import { Sounds } from './ui/Sounds';
import { Settings } from './ui/Settings';
import { Save } from './save/save';

const save = new Save();
const sim = new Sim(save.loadOrNew());
// Player-facing settings (sound / graphics / controls). Persisted in
// its own localStorage key so it survives "Reset farm". Connected to
// Sounds, Diorama, and FpsCounter further down so toggles take effect
// immediately.
const settings = new Settings();

const { diorama } = bootGame('game', sim);
diorama.connectSettings(settings);

const uiRoot = document.getElementById('ui-root')!;

// ── HUD layout ───────────────────────────────────────────────────────
// Top-left:   Gear button → opens Settings modal (pauses game).
// Top-right:  InfoCard (date / time / season / money + Shop chip + energy bar).
//             Underneath: ControlsCard (static keybinds legend).
// Bottom-cen: Hotbar — tool slots numbered 1-7.
// Bottom-rt:  StorageBar — always-visible inventory chip strip.
//
// What moved out of the old right-side ActionsBar:
//   Truck    → Shop chip inside InfoCard
//   Save     → inside Settings modal
//   Skills   → 'K' keyboard shortcut
//   Rotate   → Q/E only (no HUD button — covered by ControlsCard)
//   Settings → top-left gear button
//
// The standalone EnergyBar was folded into the InfoCard.

const infoCard    = new InfoCard(uiRoot, sim);
const tripBar     = new TripBar(uiRoot, sim);
const objective   = new Objective(uiRoot, sim);
const plotToolbar = new PlotToolbar(uiRoot, sim);
const storageBar  = new StorageBar(uiRoot, sim);
const hintToast   = new HintToast(uiRoot, sim);
const controlsCard = new ControlsCard(uiRoot);
void controlsCard;

const exitPlot = new ExitPlotButton(uiRoot, sim);
void exitPlot;
const zoomExit = new ZoomExitButton(uiRoot, sim);
void zoomExit;
const fpsCounter = new FpsCounter(uiRoot);
fpsCounter.attachDiorama(diorama);
// Reflect the current settings value on first paint, then keep in
// sync as the toggle is flipped.
fpsCounter.setVisible(settings.showFps());
settings.subscribe(() => fpsCounter.setVisible(settings.showFps()));
const tileBars = new TileProgressBars(uiRoot, sim, diorama);
const zonesPanel = new ZonesPanel(uiRoot, sim);
const plotPanel = new PlotPanel(uiRoot, sim);

// Panel host (one container, multiple registered panels)
const panelHost = document.createElement('div');
panelHost.className = 'ui-panel-host';
uiRoot.appendChild(panelHost);

const panels = new PanelManager(panelHost, sim);
panels.register(new SeedsPanel(sim));
panels.register(new BuildPanel(sim));
const storagePanel = new StoragePanel(sim);
panels.register(storagePanel, (e) => storagePanel.setFocus(e.col, e.row));
panels.register(new TruckPanel(sim));
panels.register(new MailboxPanel(sim));
const millPanel = new MillPanel(sim);
panels.register(millPanel, (e) => millPanel.setMill(e.col, e.row));
panels.register(new SkillsPanel(sim));
panels.register(new ToolRackPanel(sim));
panels.register(new SettingsPanel(sim, settings, save));
const plotConfirmPanel = new PlotConfirmPanel(sim);
panels.register(plotConfirmPanel);

// Wire the Shop chip in InfoCard to the panel manager.
infoCard.connectPanels(panels);

// Top-left gear button — direct entry to the Settings modal. Standalone
// affordance separated from the rest of the HUD chrome because opening
// settings pauses the game, which is a deliberate action.
const settingsButton = new SettingsButton(uiRoot, panels);
void settingsButton;

// Auto-open the plot-confirm panel any time the player enters selection
// mode. Close it on plot enter / cancel.
sim.bus.on(e => {
  if (e.type === 'plotSelectChanged') {
    // The legacy plot-confirm panel only auto-opens when zones manager
    // is NOT active — in zones mode the ZonesPanel handles the new-plot
    // flow inline.
    if (sim.state.selecting && !sim.state.zonesMode) panels.open('plot-confirm');
    else if (panels.activeId() === 'plot-confirm') panels.close();
  } else if (e.type === 'plotEntered') {
    if (panels.activeId() === 'plot-confirm') panels.close();
    document.body.classList.add('plot-mode');
    panels.close();
  } else if (e.type === 'plotExited') {
    document.body.classList.remove('plot-mode');
  }
});

// Hotbar (bottom-center, tools only)
const hotbar = new Hotbar(uiRoot, sim);

// FX — InfoCard exposes the `target(id)` interface FlyToHud needs.
const fly = new FlyToHud(uiRoot, sim, diorama, infoCard);
const sounds = new Sounds(sim);
// Hand the Diorama to Sounds so positional plays + the truck motor
// emitter can read the camera target / zoom / truck position per frame.
sounds.connectListener(diorama);
// Wire the Settings store so the master volume slider + mute toggle
// in the settings panel drive the audio bus immediately.
sounds.connectSettings(settings);
void sounds;

// Global keybinds that aren't camera-related (camera Q/E/T/G live in
// Diorama). Skipped when the player is typing in an input so the
// volume slider, zone-rename field, etc. don't get hijacked.
window.addEventListener('keydown', (e) => {
  const ae = document.activeElement;
  if (
    ae instanceof HTMLInputElement ||
    ae instanceof HTMLTextAreaElement ||
    (ae as HTMLElement | null)?.isContentEditable
  ) return;
  if (e.key === 'k' || e.key === 'K') {
    e.preventDefault();
    panels.toggle('skills');
  } else if (e.key === 'Escape' && panels.activeId() === 'settings') {
    panels.close();
  }
});

// Autosave on every meaningful event.
let dirty = false;
sim.bus.on(e => {
  if (e.type === 'harvested' || e.type === 'planted' || e.type === 'fieldPlaced' ||
      e.type === 'fieldDried'  || e.type === 'watered'  ||
      e.type === 'sold' || e.type === 'buildingPlaced' || e.type === 'chopped' ||
      e.type === 'constructionAdvanced' || e.type === 'constructionComplete' ||
      e.type === 'millStarted'  || e.type === 'millFinished' || e.type === 'leveledUp' ||
      e.type === 'truckArrived' ||
      e.type === 'truckDeparted' || e.type === 'truckReturned' ||
      e.type === 'truckLoaded'   || e.type === 'platformChanged' ||
      e.type === 'orderPlaced'   || e.type === 'orderDelivered' ||
      e.type === 'seedsBought'   || e.type === 'propMoved' ||
      e.type === 'plotEntered'      || e.type === 'plotExited'     ||
      e.type === 'weedPlucked'      || e.type === 'subCellTilled'  ||
      e.type === 'subCellSeeded'    || e.type === 'subCellWatered' ||
      e.type === 'subCellHarvested' || e.type === 'foraged'        ||
      e.type === 'berryPlanted'     || e.type === 'saplingMatured' ||
      e.type === 'acornPlanted'     || e.type === 'treeSaplingMatured' ||
      e.type === 'plotTileRemoved'  || e.type === 'mushroomGrew'   ||
      e.type === 'berryGrew') {
    dirty = true;
  }
});

// Single rAF loop
let prev = performance.now();
function tick(now: number) {
  const dt = Math.min((now - prev) / 1000, 0.05);
  prev = now;
  diorama.update(dt);
  infoCard.update();
  panels.tick();
  hintToast.update(dt);
  objective.update(dt);
  plotToolbar.update(dt);
  storageBar.update();
  tripBar.update(dt);
  fly.update(dt);
  tileBars.update(dt);
  zonesPanel.update(dt);
  plotPanel.update(dt);
  fpsCounter.update(dt, now);
  // Hotbar is event-driven; no per-frame work.
  if (dirty) {
    save.write(sim.state);
    dirty = false;
  }
  requestAnimationFrame(tick);
}
requestAnimationFrame(tick);

// Reliable save triggers.
const flush = () => save.write(sim.state);
window.addEventListener('beforeunload', flush);
window.addEventListener('pagehide', flush);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flush();
});

(window as any).__sim = sim;
(window as any).__diorama = diorama;
(window as any).__panels = panels;
void hotbar;
